import { useToast } from '@chakra-ui/react'
import { useStytch, useStytchSession } from '@stytch/react'
import {
  SelectProduct as SelectProductRoute,
  UserQueryKey,
} from 'api/common/routes'
import axios from 'axios'
import {
  InsuranceFormValues,
  OTHER_PAYER_VALUE,
} from 'containers/Onboarding/InsuranceInfo/InsuranceForm'
import useInsurance from 'hooks/useInsuranceCard'
import useSearchParams from 'hooks/useSearchParams'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  createContext,
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from 'react-query'

export type Program =
  | 'nutrition-only'
  | 'diagnostic'
  | 'full-program'
  | 'insurance'
  | 'insurance-diagnostic'
  | 'mnc'

export function isProgramType(input: string): input is Program {
  return [
    'nutrition-only',
    'diagnostic',
    'full-program',
    'insurance',
    'insurance-diagnostic',
    'mnc',
  ].includes(input)
}

interface MetadataType {
  title: string
  type: Program
  program: string
  savings?: string
  benefits: string
  agreementNote?: string
  hidden?: string
  commitment?: string
}

interface RecurringType {
  interval: string
  interval_count: number
}

export interface PaymentPlan {
  currency: string
  id: string
  unit_amount: number
  metadata: MetadataType
  recurring: RecurringType
  product: string
}

interface OnboardingContextInterface {
  selectedPlan: PaymentPlan | undefined
  selectedProgram: Program | undefined
  paymentDone: boolean
  insuranceDone: boolean
  acceptedTerms: boolean
  insuranceDetails: MutableRefObject<InsuranceFormValues | undefined>
  uploadInsuranceDetails: (onSubmit?: () => void) => Promise<void>
  setSelectedPlan: (plan: PaymentPlan) => void
  setSelectedProgram: (program?: Program) => void
  toggleAcceptedTerms: () => void
  markPaymentComplete: () => void
  markInsuranceComplete: () => void
  setVariantLabel: (label: string) => void
  variantLabel: string
}

export const OnboardingContext = createContext<OnboardingContextInterface>(
  {} as OnboardingContextInterface
)

interface OnboardingProviderProps {
  children: React.ReactNode
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const stytch = useStytch()
  const { session } = useStytchSession()
  const queryClient = useQueryClient()
  const [paymentDone, _setPaymentDone] = useState<boolean>(false)
  const [acceptedTerms, _setAcceptedTerms] = useState<boolean>(false)
  const [insuranceDone, _setInsuranceDone] = useState<boolean>(false)
  const [selectedPlan, _setSelectedPlan] = useState<PaymentPlan | undefined>(
    undefined
  )
  const [selectedProgram, _setSelectedProgram] = useState<Program | undefined>(
    undefined
  )
  const [variantLabel, _setVariantLabel] = useState<string>('')
  const markPaymentComplete = () => _setPaymentDone(true)
  const markInsuranceComplete = () => _setInsuranceDone(true)
  const toggleAcceptedTerms = () => _setAcceptedTerms((prev) => !prev)
  const setSelectedPlan = (plan: PaymentPlan) => _setSelectedPlan(plan)
  const setVariantLabel = (label: string) => _setVariantLabel(label)
  const searchParams = useSearchParams()
  const variantLabelParam = searchParams.get('variant_label')
  const insuranceDetails = useRef<InsuranceFormValues | undefined>(undefined)
  const { handleUploadInsuranceData } = useInsurance()
  const toast = useToast()
  const { enableElationAccountCreationAtPayment } = useFlags()

  useEffect(() => {
    if (variantLabelParam) {
      _setVariantLabel(variantLabelParam)
    }
  }, [variantLabelParam])

  const setSelectedProgram = (program?: Program) => {
    _setSelectedProgram(program)

    if (program) {
      saveSelectedProgramToDatabase(program)
    }
  }

  const saveSelectedProgramToDatabase = async (program: Program) => {
    if (!session) {
      return
    }

    const tokens = stytch.session.getTokens()
    const session_jwt = tokens ? tokens.session_jwt : undefined
    await axios.patch(
      `${process.env.REACT_APP_SERVER_URL}${SelectProductRoute}`,
      { program },
      { headers: { Authorization: `Bearer ${session_jwt}` } }
    )
    // Invalidate user query to force refetch user with fresh data
    queryClient.invalidateQueries(UserQueryKey)
  }

  const uploadInsuranceDetails = async (onSubmit?: () => void) => {
    // we use this flag for a better toggling experience for the experiment.
    // TODO: We should remove this if statement usage once we have confirmed that the experiment is successful.
    if (!enableElationAccountCreationAtPayment) {
      return
    }

    try {
      if (!insuranceDetails.current) {
        throw new Error('Insurance details are not available')
      }

      const addressLine2 = insuranceDetails.current
        .insured_person_address_line_2
        ? ` ${insuranceDetails.current.insured_person_address_line_2}`
        : ''
      const insured_person_address = insuranceDetails.current
        ?.insured_person_address_line_1
        ? `${insuranceDetails.current.insured_person_address_line_1}${addressLine2}`
        : undefined

      const dataUploadResponse = await handleUploadInsuranceData({
        ...insuranceDetails.current,
        payer_id:
          insuranceDetails.current.payer_id === OTHER_PAYER_VALUE
            ? undefined
            : insuranceDetails.current.payer_id,
        insured_person_address,
        relationship_to_insured:
          insuranceDetails.current.relationship_to_insured === 'Self'
            ? undefined
            : insuranceDetails.current.relationship_to_insured,
      })

      if (
        !dataUploadResponse ||
        (dataUploadResponse.status !== 201 && dataUploadResponse.status !== 200)
      ) {
        toast({
          title: 'An error occurred',
          description:
            'We were unable to upload your insurance card. Please try again or email concierge@allarahealth.com if the problem persists',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      onSubmit?.()
    } catch (error) {
      toast({
        title: 'An error occurred',
        description:
          'We were unable to upload your insurance card. Please try again or email concierge@allarahealth.com if the problem persists',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <OnboardingContext.Provider
      value={{
        paymentDone,
        acceptedTerms,
        insuranceDone,
        selectedPlan,
        selectedProgram,
        variantLabel,
        insuranceDetails,
        uploadInsuranceDetails,
        setSelectedPlan,
        setSelectedProgram,
        toggleAcceptedTerms,
        markPaymentComplete,
        markInsuranceComplete,
        setVariantLabel,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboardingContext = (): OnboardingContextInterface =>
  useContext(OnboardingContext)
